/* eslint-disable */
/**
 * Partners — "For Partners" chooser page.
 *
 * Session 60.3 · Beta feedback: partner-side CTAs are too hidden. This page
 * is the single, discoverable entry point for both partner types:
 *   • List a Workspace — coworking, meeting rooms, offices, event spaces
 *   • Join Métier Hours — cafés + local businesses in the perks program
 *
 * Both CTAs route to the EXISTING onboarding flows (no duplicate signup
 * systems). Every button click is tracked via `/api/track/nav-cta` so we
 * can measure whether the new prominent placement lifts conversion.
 *
 * No em dashes anywhere in this component's copy per Cam's Feb 2026 rule.
 */
import { Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import { trackNavCta } from "../lib/navCta";

const JOIN_HOURS_URL =
  "mailto:contact@workmetier.com?subject=Join%20M%C3%A9tier%20Hours%20%2F%20Caf%C3%A9%20Partner" +
  "&body=Café%20name%3A%20%0AAddress%3A%20%0AContact%20name%3A%20%0AWhy%20we%27d%20be%20a%20good%20fit%3A%20";

export default function Partners() {
  return (
    <AppShell>
      <div className="m-page-pad" style={{ maxWidth: 1080, margin: "0 auto" }}>
        <div style={{ maxWidth: 720 }}>
          <span className="m-eyebrow">Partner with Métier</span>
          <h1 className="m-h1">
            Grow your reach with <em>Métier</em>.
          </h1>
          <p className="m-lede" style={{ marginBottom: 32 }}>
            Métier is a local workspace and café directory plus community
            platform. Add your space or business and reach a curated
            community of professionals looking for places to work, meet,
            and connect nearby.
          </p>
        </div>

        <div
          data-testid="partners-options"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: 24,
            marginTop: 24,
          }}
        >
          {/* Option 1 — List a Workspace */}
          <article
            className="m-card"
            data-testid="partner-option-workspace"
            style={{
              padding: "28px 26px",
              display: "flex",
              flexDirection: "column",
              gap: 14,
            }}
          >
            <div
              style={{
                fontSize: 11,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: "var(--gold)",
                fontWeight: 600,
              }}
            >
              Option 1
            </div>
            <h2
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 26,
                margin: 0,
                lineHeight: 1.2,
              }}
            >
              List a Workspace
            </h2>
            <p style={{ fontSize: 15, lineHeight: 1.65, opacity: 0.85, margin: 0 }}>
              Have meeting rooms, offices, event space, or flexible workspace?
              Add your location to Métier so members can discover and book it.
            </p>
            <ul
              style={{
                margin: 0,
                paddingLeft: 20,
                fontSize: 14,
                lineHeight: 1.7,
                opacity: 0.78,
              }}
            >
              <li>Coworking spaces</li>
              <li>Meeting rooms and day offices</li>
              <li>Event spaces</li>
              <li>Flexible or hybrid workspace</li>
            </ul>
            <div style={{ marginTop: "auto", paddingTop: 12 }}>
              <Link
                to="/list-your-space"
                onClick={() => trackNavCta("list_workspace")}
                className="m-btn-gold"
                data-testid="partners-cta-list-workspace"
                style={{ display: "inline-block" }}
              >
                List a Workspace &rarr;
              </Link>
            </div>
          </article>

          {/* Option 2 — Join Métier Hours */}
          <article
            className="m-card"
            data-testid="partner-option-hours"
            style={{
              padding: "28px 26px",
              display: "flex",
              flexDirection: "column",
              gap: 14,
            }}
          >
            <div
              style={{
                fontSize: 11,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: "var(--gold)",
                fontWeight: 600,
              }}
            >
              Option 2
            </div>
            <h2
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 26,
                margin: 0,
                lineHeight: 1.2,
              }}
            >
              Add Your Café
            </h2>
            <p style={{ fontSize: 15, lineHeight: 1.65, opacity: 0.85, margin: 0 }}>
              Own or manage a work-friendly café? Join Métier Hours and reach
              remote and hybrid professionals looking for a great place to
              settle in and get work done.
            </p>
            <ul
              style={{
                margin: 0,
                paddingLeft: 20,
                fontSize: 14,
                lineHeight: 1.7,
                opacity: 0.78,
              }}
            >
              <li>Independent cafés and coffee shops</li>
              <li>Restaurants with work-friendly seating</li>
              <li>Local businesses offering member perks</li>
            </ul>
            <div style={{ marginTop: "auto", paddingTop: 12 }}>
              <a
                href={JOIN_HOURS_URL}
                onClick={() => trackNavCta("join_hours")}
                className="m-btn-gold"
                data-testid="partners-cta-join-hours"
                style={{ display: "inline-block" }}
              >
                Join Métier Hours &rarr;
              </a>
            </div>
          </article>
        </div>

        <div
          style={{
            marginTop: 40,
            padding: "20px 24px",
            background: "rgba(184,145,58,0.08)",
            border: "1px solid rgba(184,145,58,0.22)",
            borderRadius: 10,
            fontSize: 14,
            lineHeight: 1.6,
            opacity: 0.9,
          }}
        >
          Not sure which fits? Reply to any Métier email or reach us at{" "}
          <a
            href="mailto:contact@workmetier.com"
            style={{ color: "var(--gold)", textDecoration: "none", fontWeight: 600 }}
          >
            contact@workmetier.com
          </a>{" "}
          and we&rsquo;ll help you pick the right path.
        </div>
      </div>
    </AppShell>
  );
}
